import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "../services/validations/resolvers/joiResolver";
import schema from "../services/validations/registrationSchema";
import Notice from "./Notice";
import Link from "next/link";
import { useRecaptcha } from "../services/hooks/useRecaptcha";
import { useIsEmailAddressAvailable } from "../services/hooks/useIsEmailAddressAvailable";

export default React.memo(function RegistrationForm({
  onSignUp,
  signUpError,
  signUpStatus,
}) {
  const { handleSubmit, register, errors, formState } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: joiResolver(schema),
  });

  const { isValid: isRegistrationFormValid } = formState;

  const [registrationData, setRegistrationData] = useState();

  const { executeRecaptcha, isSafeRequest, recaptchaStatus } = useRecaptcha();

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const {
    isEmailAddressAvailable,
    isEmailAddressAvailableStatus,
    checkEmailAddressAvailability,
  } = useIsEmailAddressAvailable();

  const [
    displayEmailAddressAvailabilityError,
    setDisplayEmailAddressAvailabilityError,
  ] = useState(false);

  const onSubmit = (data) => {
    if (displayEmailAddressAvailabilityError) {
      return;
    }

    if (isRegistrationFormValid === true) {
      executeRecaptcha("registration_request");

      setRegistrationData(data);
    }
  };

  const signUp = (data) => {
    onSignUp(data);
  };

  const handleEmailAddressBlur = (e) => {
    const value = e.target.value || "";

    checkEmailAddressAvailability(value);
  };

  useEffect(() => {
    if (recaptchaStatus === "ready" && isSafeRequest && registrationData) {
      signUp(registrationData);
    }
  }, [recaptchaStatus, isSafeRequest, registrationData]);

  useEffect(() => {
    setIsSubmittingForm(
      signUpStatus === "pending" ||
        recaptchaStatus === "pending" ||
        (recaptchaStatus === "ready" && signUpStatus === "idle")
    );
  }, [recaptchaStatus, signUpStatus]);

  useEffect(() => {
    setDisplayEmailAddressAvailabilityError(
      isEmailAddressAvailableStatus === "ready" && !isEmailAddressAvailable
    );
  }, [isEmailAddressAvailable, isEmailAddressAvailableStatus]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {signUpError && <Notice type="danger" message={signUpError} />}
      <div className="row">
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label className="sr-only" htmlFor="firstName">
              First Name
            </label>
            <input
              ref={register}
              className={`form-control form-control-sm ${
                errors.firstName ? "is-invalid" : ""
              }`}
              id="firstName"
              name="firstName"
              type="text"
              placeholder="First Name"
            />
            {errors.firstName && (
              <span className="input-error">First name is required.</span>
            )}
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label className="sr-only" htmlFor="lastName">
              Last Name
            </label>
            <input
              ref={register}
              className={`form-control form-control-sm ${
                errors.lastName ? "is-invalid" : ""
              }`}
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Last Name"
            />
            {errors.lastName && (
              <span className="input-error">Last name is required.</span>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="sr-only" htmlFor="email">
              Email Address
            </label>
            <input
              ref={register}
              className={`form-control form-control-sm ${
                errors.email ? "is-invalid" : ""
              }`}
              id="email"
              name="email"
              type="email"
              placeholder="Email Address"
              onBlur={handleEmailAddressBlur}
            />

            {displayEmailAddressAvailabilityError ? (
              <span className="input-error">
                An account already exists with this email address.{" "}
                <Link href="/login">
                  <a>Sign into an existing account.</a>
                </Link>
              </span>
            ) : (
              errors.email && (
                <span className="input-error">
                  Please enter a valid email address
                </span>
              )
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="sr-only" htmlFor="password">
              Password
            </label>
            <input
              ref={register}
              className={`form-control form-control-sm ${
                errors.password ? "is-invalid" : ""
              }`}
              id="password"
              name="password"
              type="password"
              placeholder="Password"
            />
            {errors.password && (
              <span className="input-error">
                Passwords must be 6 characters or more.
              </span>
            )}
          </div>
        </div>
        <div className="col-12 col-md-auto mx-auto">
          <div className="form-group font-size-sm text-center">
            Already have an account?{" "}
            <Link href="/login">
              <a>Sign In</a>
            </Link>
          </div>
        </div>
        <div className="col-12">
          <button
            className="btn btn-sm btn-dark btn-block"
            type="submit"
            disabled={isSubmittingForm}
          >
            {isSubmittingForm ? "Please wait..." : "Register"}
          </button>

          <div className="form-group font-size-sm text-muted text-center mt-2">
            <span className="d-block">
              By clicking "Register", you agree to our{" "}
              <Link href="/terms">
                <a>Terms of Service</a>
              </Link>
              , and{" "}
              <Link href="/privacy">
                <a>Privacy & Cookie Policy</a>
              </Link>
              .
            </span>

            <span className="d-block text-muted">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy" target="_blank">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms" target="_blank">
                Terms of Service
              </a>{" "}
              also apply.
            </span>
          </div>
        </div>
      </div>
    </form>
  );
});
